import { Controller } from "@hotwired/stimulus"

/** GeoJSON: simplified flow — Upload creates Paintbrush; then choose to open it or close. */
export default class extends Controller {
  static targets = [
    "fileInput",
    "geoserverHelp",
    "geojsonHelp",
    "standardOptions",
    "choicePanel",
    "geojsonActiveStep",
    "geojsonSuccessStep",
    "uploadGeojsonButton",
    "submitButton",
  ]
  static values = { quickCreateUrl: String }

  connect() {
    this.lastPaintbrushUrl = null
    this.syncPanel()
  }

  fileChanged() {
    this.syncPanel()
  }

  syncPanel() {
    if (!this.hasFileInputTarget) return
    const file = this.fileInputTarget.files?.[0]
    const geo = !!(file && this.isGeoJsonFile(file))

    if (this.hasGeoserverHelpTarget) {
      this.geoserverHelpTarget.classList.toggle("d-none", geo)
    }
    if (this.hasGeojsonHelpTarget) {
      this.geojsonHelpTarget.classList.toggle("d-none", !geo)
    }
    if (this.hasStandardOptionsTarget) {
      this.standardOptionsTarget.classList.toggle("d-none", geo)
    }
    if (this.hasChoicePanelTarget) {
      this.choicePanelTarget.classList.toggle("d-none", !geo)
    }

    if (geo) {
      this.resetGeojsonSuccessUi()
    } else {
      this.lastPaintbrushUrl = null
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = geo
    }
  }

  resetGeojsonSuccessUi() {
    if (this.hasGeojsonActiveStepTarget) {
      this.geojsonActiveStepTarget.classList.remove("d-none")
    }
    if (this.hasGeojsonSuccessStepTarget) {
      this.geojsonSuccessStepTarget.classList.add("d-none")
    }
  }

  interceptSubmit(event) {
    const file = this.hasFileInputTarget ? this.fileInputTarget.files?.[0] : null
    if (file && this.isGeoJsonFile(file)) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  closeGeojsonPanel() {
    if (!this.hasFileInputTarget) return
    this.lastPaintbrushUrl = null
    this.fileInputTarget.value = ""
    this.fileInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.syncPanel()
  }

  async uploadGeojson() {
    const file = this.hasFileInputTarget ? this.fileInputTarget.files?.[0] : null
    if (!file || !this.isGeoJsonFile(file)) return

    const btn = this.uploadGeojsonButtonTarget
    const originalHtml = btn.innerHTML
    btn.disabled = true
    btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i>Uploading…'

    try {
      const url = await this.createPaintbrushAndUpload(file)
      this.lastPaintbrushUrl = url
      if (this.hasGeojsonActiveStepTarget) {
        this.geojsonActiveStepTarget.classList.add("d-none")
      }
      if (this.hasGeojsonSuccessStepTarget) {
        this.geojsonSuccessStepTarget.classList.remove("d-none")
      }
    } catch (error) {
      console.error(error)
      alert(`Could not create Paintbrush: ${error.message}`)
    } finally {
      btn.disabled = false
      btn.innerHTML = originalHtml
    }
  }

  openPaintbrush() {
    if (!this.lastPaintbrushUrl) return
    if (window.parent !== window) {
      window.parent.postMessage({ type: "team-data-modal-close" }, window.location.origin)
    }
    window.top.location.href = this.lastPaintbrushUrl
  }

  closeAfterSuccess() {
    if (window.parent !== window) {
      window.parent.postMessage({ type: "team-data-modal-close" }, window.location.origin)
    }
    this.closeGeojsonPanel()
  }

  isGeoJsonFile(file) {
    const n = (file.name || "").toLowerCase()
    const t = (file.type || "").toLowerCase()
    return n.endsWith(".geojson") || t === "application/geo+json" || t === "application/vnd.geo+json"
  }

  /** @returns {Promise<string>} paintbrush URL */
  async createPaintbrushAndUpload(file) {
    const fileContent = await file.text()
    try {
      JSON.parse(fileContent)
    } catch {
      throw new Error("Invalid JSON in file")
    }

    const fileName = file.name
    const annotationName = fileName.toLowerCase().endsWith(".geojson")
      ? fileName.slice(0, -8)
      : fileName

    const csrfToken = this.csrfToken()
    if (!csrfToken) throw new Error("CSRF token missing; refresh the page.")
    if (!this.quickCreateUrlValue) throw new Error("Quick create URL missing; refresh the page.")

    const formData = new FormData()
    formData.append("authenticity_token", csrfToken)

    const createResponse = await fetch(this.quickCreateUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
      body: formData,
      credentials: "same-origin",
    })

    if (!createResponse.ok) {
      const errText = await createResponse.text().catch(() => "")
      throw new Error(`Create failed (${createResponse.status}): ${errText.slice(0, 200)}`)
    }

    const annotationIdMatch = createResponse.url.match(/\/paintbrush\/(\d+)/) || createResponse.url.match(/\/annotations\/(\d+)/)
    if (!annotationIdMatch) {
      throw new Error("Could not read Paintbrush id from response.")
    }
    const annotationId = annotationIdMatch[1]

    const updateFormData = new FormData()
    updateFormData.append("annotation[name]", annotationName)
    await fetch(`/paintbrush/${annotationId}`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrfToken },
      body: updateFormData,
      credentials: "same-origin",
    })

    const geojsonFormData = new FormData()
    geojsonFormData.set("geojson", fileContent)
    const uploadResponse = await fetch(`/paintbrush/${annotationId}/save_geojson`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": csrfToken },
      body: geojsonFormData,
      credentials: "same-origin",
    })

    if (!uploadResponse.ok) {
      await this.deleteAnnotation(annotationId)
      const errText = await uploadResponse.text()
      throw new Error(errText.slice(0, 200) || `Upload failed (${uploadResponse.status})`)
    }

    return new URL(`/paintbrush/${annotationId}`, window.location.origin).href
  }

  async deleteAnnotation(annotationId) {
    const csrfToken = this.csrfToken()
    try {
      await fetch(`/paintbrush/${annotationId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken, Accept: "application/json" },
        credentials: "same-origin",
      })
    } catch (e) {
      console.warn(e)
    }
  }

  csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]')
    return el ? el.getAttribute("content") : ""
  }
}
